// Warm-up of the lazy screens, so the FIRST tap on the Games icon, a gift, the wallet, a game ... opens at once instead of waiting
// for the screen's code to download (that download was the "tap, then nothing for a second" delay).
// Runs quietly when the phone is idle, one chunk at a time, and never on Data Saver.

type Loader = () => Promise<unknown>
const LOADERS: Loader[] = [
  () => import('@/components/games'),
  () => import('@/components/premium-store'),   // gifts / creator support / Green Tick / theme purchase
  () => import('@/components/wallet'),
  () => import('@/components/emoji3d'),
  () => import('@/components/game-chat'),
  () => import('@/components/ludo'),
  () => import('@/components/chess'),
  () => import('@/components/carrom'),
  () => import('@/components/avatar-picker'),
]

// Light screens warm first; the heavy game bundles (ludo / chess / carrom / emoji3d / game-chat) wait ~12 s, so that download
// no longer competes with scrolling right after launch on low-end phones. They are still ready before the first tap.
const LIGHT = [0, 1, 2, 8]
const HEAVY = [3, 4, 5, 6, 7]

let started = false
const idle = (fn: () => void, timeout = 3000) => {
  const w = window as any
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fn, { timeout })
  else setTimeout(fn, 400)
}

export function warmScreens() {
  if (started || typeof window === 'undefined') return
  started = true
  const c = (navigator as any).connection
  if (c && (c.saveData || /(^|-)2g$/.test(String(c.effectiveType || '')))) return
  const run = (order: number[], done?: () => void) => {
    let k = 0
    const next = () => {
      if (k >= order.length) { done?.(); return }
      const load = LOADERS[order[k++]]
      Promise.resolve().then(load).catch(() => undefined).finally(() => idle(next))
    }
    idle(next)
  }
  // let the first screen settle before downloading anything extra
  setTimeout(() => run(LIGHT, () => setTimeout(() => run(HEAVY), 8000)), 4000)
}

/** Warm one screen right now (call from onPointerDown of its button: the finger is on its way down, the code starts loading ~100ms before the click). */
export function warmNow(i: number) { try { void LOADERS[i]?.() } catch { /* ignore */ } }

/** A no-op touch listener makes iOS apply :active (press feedback) instantly; passive, so it never blocks scrolling. */
let tapReady = false
export function enableTapFeedback() {
  if (tapReady || typeof document === 'undefined') return
  tapReady = true
  document.addEventListener('touchstart', () => undefined, { passive: true })
}
